import { type FormEvent, useEffect, useState } from 'react';
import { i18n, shareOrderSchema } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDateTime } from '../lib/format.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

type ShareHistory = { id: string; recipient: string; sentAt: string }[];

export function ShareOrderDialog({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ShareHistory>([]);
  const [historyError, setHistoryError] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  useEffect(() => {
    let active = true;
    api<ShareHistory>(`/orders/${orderId}/share-history`)
      .then((rows) => {
        if (active) {
          setHistory(rows);
          setHistoryError(false);
        }
      })
      .catch(() => {
        if (active) setHistoryError(true);
      });
    return () => {
      active = false;
    };
  }, [orderId, historyVersion]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || sent) return;
    const parsed = shareOrderSchema.safeParse({ email });
    if (!parsed.success) {
      setError(t('order.share.invalid_email'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api(`/orders/${orderId}/share`, { method: 'POST', body: parsed.data });
      setSent(true);
      setHistoryVersion((version) => version + 1);
    } catch {
      setError(t('order.share.error'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog title={t('order.share.title')} onClose={busy ? () => undefined : onClose}>
      <form noValidate onSubmit={(event) => void submit(event)} className="space-y-4 text-sm">
        <p>{t('order.share.help')}</p>
        {history.length > 0 && (
          <section aria-label={t('order.share.history')} className="space-y-1">
            <h3 className="font-semibold">{t('order.share.history')}</h3>
            <ul className="max-h-48 overflow-auto">
              {history.map((entry) => (
                <li key={entry.id} className="flex flex-wrap justify-between gap-2 border-b py-1">
                  <span className="break-all">{entry.recipient}</span>
                  <time dateTime={entry.sentAt}>{formatDateTime(entry.sentAt)}</time>
                </li>
              ))}
            </ul>
          </section>
        )}
        {historyError && <p role="status">{t('order.share.history_error')}</p>}
        <label className="block space-y-1">
          <span>{t('order.share.email')}</span>
          <input
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            value={email}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setSent(false);
              setError(null);
            }}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        {error && (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        )}
        {sent && (
          <p role="status" aria-live="polite">
            {t('order.share.sent')}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded border px-3 py-1.5"
          >
            {t('common.close')}
          </button>
          {!sent && (
            <button
              type="submit"
              disabled={busy}
              className="rounded border px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t(busy ? 'order.share.sending' : 'order.share.send')}
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
}
